<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportMetricCard from './ReportMetricCard.vue';
import ReportsAPI from 'dashboard/api/reports';
import ReportLiveCountBanner from 'dashboard/components-next/reports/ReportLiveCountBanner.vue';
import { toast } from 'dashboard/helper/toast';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const EMPTY = '--';
const conversationCount = ref(EMPTY);
const messageCount = ref(EMPTY);
const resolutionRate = ref(EMPTY);
const handoffRate = ref(EMPTY);
// Período fora dos agregados (bot_metrics_coverage): pedido leve em paralelo,
// pra linha aparecer ENQUANTO os cartões contam. Falhou = `{ failed: true }`.
const coverage = ref(null);
// Resposta de um filtro anterior que chega atrasada não sobrescreve a atual.
let request = 0;

const formatToPercent = value => {
  return value && value !== EMPTY ? `${value}%` : EMPTY;
};

const fetchCoverage = current => {
  coverage.value = null;
  ReportsAPI.getBotMetricsCoverage(props.filters)
    .then(({ data }) => {
      if (current === request) coverage.value = data.rollup;
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('[BotMetrics] cobertura falhou', error);
      if (current === request) coverage.value = { failed: true };
    });
};

const fetchMetrics = () => {
  if (!props.filters.to || !props.filters.from) {
    return;
  }
  request += 1;
  const current = request;
  // Os números do filtro anterior não ficam na tela como se fossem deste.
  [conversationCount, messageCount, resolutionRate, handoffRate].forEach(
    value => {
      value.value = EMPTY;
    }
  );
  fetchCoverage(current);
  ReportsAPI.getBotMetrics(props.filters)
    .then(response => {
      if (current !== request) return;
      conversationCount.value =
        response.data.conversation_count.toLocaleString();
      messageCount.value = response.data.message_count.toLocaleString();
      resolutionRate.value = response.data.resolution_rate.toString();
      handoffRate.value = response.data.handoff_rate.toString();
    })
    .catch(() => {
      if (current === request) toast.error(t('REPORT.DATA_FETCHING_FAILED'));
    });
};

watch(() => props.filters, fetchMetrics, { deep: true });

onMounted(fetchMetrics);
</script>

<template>
  <ReportLiveCountBanner :coverage="coverage" />
  <div
    class="flex flex-wrap mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.TOOLTIP')"
      :value="conversationCount"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
      :value="messageCount"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.TOOLTIP')"
      :value="formatToPercent(resolutionRate)"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.TOOLTIP')"
      :value="formatToPercent(handoffRate)"
      class="flex-1"
    />
  </div>
</template>
